/**
 * Re-runs <video> source selection when the hero crosses its 750px breakpoint.
 *
 * <video> picks a source once, at initialisation, and never re-evaluates the
 * `media` attribute on resize or rotation — unlike <picture>. Rotating a phone
 * into landscape crosses 750px, so without this the portrait crop stays pinned
 * and gets stretched across a desktop-width hero. load() is the only call that
 * re-runs the resource selection algorithm.
 *
 * It also owns the hero's play button. When autoplay is refused (iOS Low Power
 * Mode, Auto-Play Video Previews off, thermal limits) or delivery stalls, the
 * video sits paused over its still, and only a tap can start it. The button
 * shows while the hero is in view and the video has not played for GRACE_MS,
 * and its click calls play() inside the user gesture those policies require.
 * The snippet renders the video without `autoplay`. On connect, unless the visitor
 * prefers reduced motion, this sets it and calls load(): load() re-runs resource
 * selection with the attribute present, so the browser's own autoplay starts it.
 * play() is never called on load, because a script-side play() would also start a
 * video the visitor has scrolled past. Under reduced motion the video stays on its
 * first frame; the play button (after the same grace period) lets the visitor start
 * it, and a crop switch only resumes a video that was playing.
 *
 * A custom element rather than an inline script: the theme editor re-renders a
 * section in place, and inline scripts re-execute and stack a second listener on
 * every save. connectedCallback/disconnectedCallback bracket the listener to the
 * element's real lifetime instead.
 *
 * See snippets/c-hero-responsive-video.liquid.
 */
(function () {
  if (customElements.get('c-hero-responsive-video')) return;

  // Matches the 750px bound in hero.liquid's own media queries and the `media`
  // attributes the snippet writes. Changing one means changing all three.
  const DESKTOP_QUERY = '(min-width: 750px)';

  // How long a visible, unplayed video waits before offering the button. A
  // normal autoplay fires `playing` well inside it; a refused one never does.
  const GRACE_MS = 2500;

  class HeroResponsiveVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('.c-hero-video__play');
      if (!this.video) return;

      this.video.addEventListener('playing', this);
      this.video.addEventListener('pause', this);
      this.video.addEventListener('waiting', this);
      // A failed <source> fires `error` at the source, not the video, and error
      // does not bubble; a capturing listener on the video still sees it.
      this.video.addEventListener('error', this, true);
      if (this.button) this.button.addEventListener('click', this);
      if (!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        this.video.autoplay = true;
        this.video.load();
      }

      // Observes the video, not this element: `display: contents` gives the host
      // no box, so it would never intersect.
      this.inView = false;
      if (this.button && window.IntersectionObserver) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[entries.length - 1].isIntersecting;
          this.arm();
        });
        this.observer.observe(this.video);
      }

      if (!window.matchMedia) return;
      // Recreated on every connect: the editor moves elements in the DOM, and a
      // query held from a previous connection would be listening for an element
      // that is no longer the one on screen.
      this.query = window.matchMedia(DESKTOP_QUERY);
      this.isDesktop = this.query.matches;
      this.query.addEventListener('change', this);
    }

    disconnectedCallback() {
      if (this.query) this.query.removeEventListener('change', this);
      this.query = null;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      clearTimeout(this.timer);
      if (!this.video) return;
      this.video.removeEventListener('playing', this);
      this.video.removeEventListener('pause', this);
      this.video.removeEventListener('waiting', this);
      this.video.removeEventListener('error', this, true);
      if (this.button) this.button.removeEventListener('click', this);
    }

    // EventTarget accepts a listener object, so there is no bound copy to store
    // and hand back to removeEventListener.
    handleEvent(event) {
      switch (event.type) {
        case 'change':
          this.switchCrop(event);
          break;
        case 'click':
          // Inside the tap's gesture. If it is refused or never starts (a
          // stall), no `playing` arrives and the button stays available.
          this.play();
          break;
        case 'pause':
        // A mid-playback stall fires `waiting` with `paused` still false; without
        // it the button, hidden on the first `playing`, would never come back.
        case 'waiting':
          this.arm();
          break;
        case 'playing':
          this.hideButton();
          break;
        case 'error':
          // A media error means a button could never work, so hide it; the still
          // stays. But a <source> whose `media` does not match also fires
          // `error` while selection moves on to the next one, so re-arm and let
          // the settled state at the end of the grace period decide.
          this.hideButton();
          this.arm();
          break;
      }
    }

    switchCrop(event) {
      if (!this.video || event.matches === this.isDesktop) return;
      this.isDesktop = event.matches;

      const wasPlaying = !this.video.paused;
      this.hideButton();
      this.video.load();
      // Muted autoplay is permitted, so this normally resumes playback. A refusal
      // is swallowed here because arm() below offers the button instead, over
      // the new crop's still.
      if (wasPlaying) this.play();
      this.arm();
    }

    play() {
      const played = this.video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }

    // (Re)starts the grace period. Decided on the video's state when it ends,
    // not on event history: the deferred script can connect after autoplay has
    // already started or a source has already failed.
    arm() {
      clearTimeout(this.timer);
      if (!this.button) return;
      if (!this.inView) {
        this.button.hidden = true;
        return;
      }
      this.timer = setTimeout(() => {
        const video = this.video;
        const failed = video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE;
        const playing = !video.paused && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
        if (this.inView && !failed && !playing) this.button.hidden = false;
      }, GRACE_MS);
    }

    hideButton() {
      clearTimeout(this.timer);
      if (this.button) this.button.hidden = true;
    }
  }

  customElements.define('c-hero-responsive-video', HeroResponsiveVideo);
})();
